import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-gym-guide/artifacts/gwvje8fa_IMG_20220211_145748_413%20%285%29.jpg";

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countryCode, setCountryCode] = useState('+351');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Preenche todos os campos');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('As palavras-passe não coincidem');
      return;
    }
    if (password.length < 6) {
      toast.error('A palavra-passe deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await register(
        name,
        email,
        password,
        phone ? `${countryCode}${phone.replace(/\s/g, '')}` : null
      );
      toast.success('Conta criada com sucesso!');
      navigate('/profile');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 pb-24 lg:pb-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 sm:mb-12 flex justify-center">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Process Body" className="w-14 h-14 rounded-full object-cover" />
              <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                PROCESS<span className="text-[#32CD32]">BODY</span>
              </span>
            </Link>
          </div>

          <div className="mb-8">
            <h1 
              className="text-3xl font-bold tracking-tight uppercase mb-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Criar Conta
            </h1>
            <p className="text-gray-400">
              Começa a tua jornada de treino personalizado
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-gray-300">
                Nome
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Seu nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-300">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-gray-300">
                Telemóvel <span className="text-gray-500">(opcional)</span>
              </Label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="h-14 w-28 bg-[#121212] border border-white/10 rounded-xl text-white px-3"
                  data-testid="register-country-code-select"
                >
                  <option value="+351">🇵🇹 +351</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+41">🇨🇭 +41</option>
                  <option value="+1">🇺🇸 +1</option>
                </select>
                <div className="relative flex-1">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="912 345 678"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Para lembretes de treino por SMS. Podes alterar depois no perfil.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-300">
                Palavra-passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">
                Confirmar Palavra-passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-14 rounded-xl text-lg"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  A criar conta...
                </>
              ) : (
                'Criar Conta'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-gray-400">
            Já tens uma conta?{' '}
            <Link to="/login" className="text-[#32CD32] hover:underline font-medium" data-testid="login-link">
              Entrar
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/8060691/pexels-photo-8060691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-3 brand-lockup" data-testid="register-brand-logo">
            <img src={LOGO_URL} alt="Process Body" className="w-12 h-12 rounded-full object-cover" />
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              PROCESS<span className="text-[#32CD32]">BODY</span>
            </span>
          </Link>
          <div>
            <h2 
              className="text-4xl font-bold tracking-tight uppercase mb-4"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Começa a tua <span className="text-[#32CD32]">transformação</span>
            </h2>
            <p className="text-gray-400">
              Planos de treino criados especialmente para ti pela IA mais avançada.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
